import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TrendingUp, ShoppingCart, Clock, AlertCircle, RefreshCw,
    ArrowUpRight, Calendar, Activity, CreditCard, User, Beaker, AlertTriangle, Timer,
} from 'lucide-react';
import { toast } from 'react-toastify';
import LabServices from '../../shared/ApiServices/LabServices';
import { LabDashboardSkeleton } from '../../shared/custom-ui/skeleton/Lab';
import {
    formatCurrency, ORDER_STATUS_META, PRIORITY_META,
    normalizeOrderPriority, extractLabOrdersList, extractLabOrderId,
    extractPatientName, type LabOrder,
} from './labUtils';

interface DashboardPeriod { revenue: number; orders: number }

interface RecentOrder {
    orderId?: string;
    PK?: string;
    patientName?: string;
    invoiceNumber?: string;
    orderStatus?: string;
    totalAmount?: string | number;
    paidAmount?: string | number;
    balanceAmount?: string | number;
    netAmount?: string | number;
    taxAmount?: string | number;
    discountAmount?: string | number;
    paymentMode?: string;
    mobile?: string;
    email?: string;
    gender?: string;
    age?: string | number;
    referenceDoctor?: string;
    orderDate?: string;
    createdAt?: string;
    comments?: string;
    priority?: string;
}

interface DashboardData {
    today: DashboardPeriod;
    thisWeek: DashboardPeriod;
    pendingResults: number;
    recentOrders: RecentOrder[];
}

const AUTO_REFRESH_MS = 90_000;

const safeNum = (v: string | number | undefined): number =>
    v == null ? 0 : Number(v);

const statusLabel = (s?: string) => {
    const key = (s ?? '').toLowerCase() as keyof typeof ORDER_STATUS_META;
    return ORDER_STATUS_META[key] ?? ORDER_STATUS_META.pending;
};

const elapsedHours = (from?: string, to?: string): number | null => {
    if (!from || !to) return null;
    const a = new Date(from).getTime();
    const b = new Date(to).getTime();
    if (isNaN(a) || isNaN(b) || b < a) return null;
    return (b - a) / 3_600_000;
};

const KpiCard: React.FC<{
    label: string;
    value: string;
    sub: string;
    Icon: React.ElementType;
    accent: string;
    accentBg: string;
    trend?: string;
}> = ({ label, value, sub, Icon, accent, accentBg, trend }) => (
    <div
        className="relative rounded-[var(--v2-radius-lg)] border border-[var(--v2-border-glass-strong)] p-5 flex flex-col gap-4 overflow-hidden group transition-[box-shadow,border-color] duration-[var(--v2-duration-normal)]"
        style={{ boxShadow: 'var(--v2-shadow-card)', background: `linear-gradient(160deg, ${accentBg} 0%, var(--v2-bg-card-inner) 45%)` }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'var(--v2-shadow-card-hover)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--v2-kpi-card-hover-border)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'var(--v2-shadow-card)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--v2-border-glass-strong)'; }}
    >
        {/* Top accent bar — gradient, not flat */}
        <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}99 100%)` }} />

        {/* Icon + label */}
        <div className="flex items-start justify-between">
            <div className="flex flex-col gap-0.5">
                <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[var(--v2-text-muted)] m-0">{label}</p>
                {trend && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md" style={{ background: accentBg, color: accent }}>
                        {trend}
                    </span>
                )}
            </div>
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                style={{ background: accentBg, border: `1px solid ${accent}30` }}>
                <Icon className="w-5 h-5" strokeWidth={2} style={{ color: accent }} />
            </div>
        </div>

        {/* Value */}
        <div className="flex flex-col gap-0.5">
            <p className="text-[30px] font-black text-[var(--v2-text-heading)] m-0 leading-none tabular-nums">{value}</p>
            <p className="text-[11px] text-[var(--v2-text-muted)] m-0">{sub}</p>
        </div>
    </div>
);

const RevenueComparison: React.FC<{ today: DashboardPeriod; week: DashboardPeriod }> = ({ today, week }) => {
    const maxRev = Math.max(today.revenue, week.revenue, 1);
    const items = [
        { label: "Today's Revenue", value: today.revenue, pct: (today.revenue / maxRev) * 100, color: '#059669' },
        { label: "This Week Revenue", value: week.revenue, pct: (week.revenue / maxRev) * 100, color: '#7C3AED' },
    ];
    const maxOrders = Math.max(today.orders, week.orders, 1);
    const orderItems = [
        { label: "Today's Orders", value: today.orders, pct: (today.orders / maxOrders) * 100, color: '#059669' },
        { label: "This Week Orders", value: week.orders, pct: (week.orders / maxOrders) * 100, color: '#7C3AED' },
    ];

    return (
        <div className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-5 flex flex-col gap-5"
            style={{ boxShadow: 'var(--v2-shadow-card)' }}>
            <div className="flex items-center justify-between">
                <p className="text-[11px] font-black uppercase tracking-widest text-[var(--v2-text-muted)] m-0">Revenue Breakdown</p>
                <div className="flex items-center gap-3 text-[10px] font-bold">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#059669] inline-block" />Today</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#7C3AED] inline-block" />This Week</span>
                </div>
            </div>

            {/* Revenue bars */}
            <div className="flex flex-col gap-3">
                {items.map(item => (
                    <div key={item.label} className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold text-[var(--v2-text-secondary)]">{item.label}</span>
                            <span className="text-[13px] font-black tabular-nums" style={{ color: item.color }}>
                                {formatCurrency(item.value)}
                            </span>
                        </div>
                        <div className="w-full h-2.5 rounded-full bg-[var(--v2-bg-surface)] overflow-hidden">
                            <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{ width: `${item.pct}%`, background: item.color }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* Divider */}
            <div className="h-px bg-[var(--v2-border-subtle)]" />

            {/* Orders section */}
            <div className="flex flex-col gap-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-[var(--v2-text-muted)] m-0 mb-2">Order Count</p>
                {orderItems.map(item => (
                    <div key={item.label} className="flex items-center justify-between text-[12px]">
                        <span className="text-[var(--v2-text-secondary)]">{item.label}</span>
                        <span className="font-black tabular-nums" style={{ color: item.color }}>{item.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const RecentOrderRow: React.FC<{ order: RecentOrder; onClick: () => void }> = ({ order, onClick }) => {
    const st = statusLabel(order.orderStatus);
    const name = order.patientName ?? 'Unknown Patient';
    const initials = name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?';
    const total = safeNum(order.totalAmount);
    const net = safeNum(order.netAmount);
    const paid = safeNum(order.paidAmount);
    const bal = safeNum(order.balanceAmount ?? (total - paid));
    const tax = safeNum(order.taxAmount);
    const disc = safeNum(order.discountAmount);
    const date = order.orderDate ?? order.createdAt?.slice(0, 10) ?? '—';

    return (
        <div
            onClick={onClick}
            className="px-4 py-4 cursor-pointer hover:bg-[var(--v2-bg-surface)] transition-colors duration-150"
        >
            {/* Row 1: Avatar + name + status */}
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-black text-white flex-shrink-0 mt-0.5"
                    style={{ background: '#059669' }}>
                    {initials}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-[13px] font-black text-[var(--v2-text-heading)] m-0 truncate">{name}</p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            {normalizeOrderPriority(order.priority) !== 'routine' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider"
                                    style={{ background: PRIORITY_META[normalizeOrderPriority(order.priority)].bg, color: PRIORITY_META[normalizeOrderPriority(order.priority)].color }}>
                                    {PRIORITY_META[normalizeOrderPriority(order.priority)].label}
                                </span>
                            )}
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider"
                                style={{ background: st.bg, color: st.color }}>
                                {order.orderStatus ?? 'pending'}
                            </span>
                        </div>
                    </div>
                    {/* Age / gender / mobile */}
                    <p className="text-[11px] text-[var(--v2-text-muted)] m-0 mt-0.5">
                        {[order.age && `${order.age}y`,
                        order.gender ? order.gender.charAt(0).toUpperCase() + order.gender.slice(1) : null,
                        order.mobile,
                        ].filter(Boolean).join(' · ')}
                    </p>
                </div>
            </div>

            {/* Row 2: Invoice + date + ref doctor */}
            <div className="flex items-start justify-between mt-2.5 pl-12 gap-4">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[var(--v2-text-muted)] m-0">Invoice</p>
                    <p className="text-[11px] font-mono font-semibold text-[var(--v2-text-primary)] m-0">{order.invoiceNumber ?? '—'}</p>
                    <p className="text-[10px] text-[var(--v2-text-muted)] m-0">{date}</p>
                </div>
                {order.referenceDoctor && (
                    <div className="text-right">
                        <p className="text-[10px] font-black uppercase tracking-widest text-[var(--v2-text-muted)] m-0">Ref Doctor</p>
                        <p className="text-[11px] font-semibold text-[var(--v2-text-primary)] m-0">{order.referenceDoctor}</p>
                    </div>
                )}
            </div>

            {/* Row 3: Finance breakdown */}
            <div className="mt-2.5 pl-12 grid grid-cols-3 gap-2 rounded-xl p-2.5"
                style={{ background: 'var(--v2-bg-surface)' }}>
                <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-[var(--v2-text-muted)] m-0">Total</p>
                    <p className="text-[12px] font-black text-[var(--v2-text-primary)] m-0 tabular-nums">{formatCurrency(total)}</p>
                    {net > 0 && net !== total && <p className="text-[9px] text-[var(--v2-text-muted)] m-0">Net: {formatCurrency(net)}</p>}
                </div>
                <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-[var(--v2-text-muted)] m-0">Paid</p>
                    <p className="text-[12px] font-black text-[#059669] m-0 tabular-nums">{formatCurrency(paid)}</p>
                    <p className="text-[9px] text-[var(--v2-text-muted)] m-0 capitalize">{order.paymentMode ?? '—'}</p>
                </div>
                <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-[var(--v2-text-muted)] m-0">Balance</p>
                    <p className="text-[12px] font-black m-0 tabular-nums"
                        style={{ color: bal > 0 ? '#DC2626' : '#059669' }}>{formatCurrency(bal)}</p>
                    {(tax > 0 || disc > 0) && (
                        <p className="text-[9px] text-[var(--v2-text-muted)] m-0">
                            {tax > 0 && `Tax: ${formatCurrency(tax)}`}
                            {tax > 0 && disc > 0 && ' · '}
                            {disc > 0 && `Disc: ${formatCurrency(disc)}`}
                        </p>
                    )}
                </div>
            </div>

            {/* Row 4: Comments if any */}
            {order.comments && (
                <p className="text-[10px] text-[var(--v2-text-muted)] italic mt-2 pl-12 m-0 line-clamp-1">
                    💬 {order.comments}
                </p>
            )}
        </div>
    );
};

export const LabDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const mountedRef = useRef(true);

    const [tab, setTab] = useState<'overview' | 'turnaround'>('overview');
    const [tatOrders, setTatOrders] = useState<LabOrder[]>([]);
    const [tatLoading, setTatLoading] = useState(false);
    const [tatLoaded, setTatLoaded] = useState(false);

    const fetchTatData = useCallback(async () => {
        setTatLoading(true);
        try {
            const res = await LabServices.listOrders({ status: 'completed', limit: 100 });
            setTatOrders(extractLabOrdersList(res));
        } catch {
        } finally {
            setTatLoading(false);
            setTatLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (tab === 'turnaround' && !tatLoaded) fetchTatData();
    }, [tab, tatLoaded, fetchTatData]);

    const fetchData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        setError('');
        try {
            const res = await LabServices.getDashboardStats();
            if (!mountedRef.current) return;

            const normalised: DashboardData = {
                today: res?.today ?? { revenue: 0, orders: 0 },
                thisWeek: res?.thisWeek ?? res?.this_week ?? { revenue: 0, orders: 0 },
                pendingResults: res?.pendingResults ?? res?.pending_results ?? 0,
                recentOrders: Array.isArray(res?.recentOrders) ? res.recentOrders
                    : Array.isArray(res?.recent_orders) ? res.recent_orders
                        : [],
            };
            setData(normalised);
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to load dashboard';
            if (!mountedRef.current) return;
            if (!silent) { setError(msg); toast.error(msg); }
        } finally {
            if (mountedRef.current) { setLoading(false); setRefreshing(false); }
        }
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        fetchData();
        const t = setInterval(() => fetchData(true), AUTO_REFRESH_MS);
        return () => { mountedRef.current = false; clearInterval(t); };
    }, [fetchData]);

    if (loading) return <LabDashboardSkeleton />;

    if (error && !data) {
        return (
            <div className="flex flex-col items-center gap-3 py-24 text-center">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.08)' }}>
                    <AlertCircle className="w-7 h-7 text-red-500" strokeWidth={1.5} />
                </div>
                <p className="text-sm font-bold text-[var(--v2-text-secondary)]">{error}</p>
                <button type="button" onClick={() => fetchData()}
                    className="px-5 py-2 rounded-xl text-xs font-bold cursor-pointer border-0"
                    style={{ background: '#059669', color: '#fff' }}>
                    Retry
                </button>
            </div>
        );
    }

    const today = data?.today ?? { revenue: 0, orders: 0 };
    const week = data?.thisWeek ?? { revenue: 0, orders: 0 };
    const pending = data?.pendingResults ?? 0;
    const orders = data?.recentOrders ?? [];

    const totalAmount = orders.reduce((s, o) => s + safeNum(o.totalAmount), 0);
    const totalBalance = orders.reduce((s, o) => {
        const bal = safeNum(o.balanceAmount ?? (safeNum(o.totalAmount) - safeNum(o.paidAmount)));
        return s + bal;
    }, 0);

    const pendingStatCount = orders.filter(o =>
        normalizeOrderPriority(o.priority) === 'stat' &&
        !['completed', 'cancelled'].includes((o.orderStatus ?? '').toLowerCase())
    ).length;

    const tatSamples = tatOrders.map(o => elapsedHours(o.orderDate ?? o.createdAt ?? (o as any).created_at, (o as any).updatedAt)).filter((h): h is number => h !== null).sort((a, b) => a - b);
    const avgTat = tatSamples.length ? tatSamples.reduce((a, b) => a + b, 0) / tatSamples.length : 0;
    const medianTat = tatSamples.length ? tatSamples[Math.floor(tatSamples.length / 2)] : 0;
    const maxTat = Math.max(1, ...tatSamples);

    return (
        <div className="flex flex-col gap-4 v2-animate-fade-in-up">

            <div
                className="rounded-2xl border border-[var(--v2-border-glass-strong)] overflow-hidden"
                style={{ background: 'var(--v2-bg-card)', boxShadow: 'var(--v2-shadow-card)' }}
            >
                <div className="h-[3px]" style={{ background: 'linear-gradient(90deg,#059669 0%,#34d399 55%,#7C3AED 100%)' }} />

                <div className="px-5 pt-4 pb-0">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0"
                                    style={{ background: 'linear-gradient(135deg,#059669 0%,#047857 100%)', boxShadow: '0 2px 8px rgba(5,150,105,0.35)' }}>
                                    <Beaker className="w-3.5 h-3.5 text-white" strokeWidth={2} />
                                </div>
                                <h1 className="text-[16px] font-black text-[var(--v2-text-heading)] m-0 tracking-tight">
                                    Lab Diagnostics
                                </h1>
                                {refreshing && (
                                    <div className="w-3.5 h-3.5 rounded-full border-2 border-[#059669] border-t-transparent animate-spin" />
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <button type="button" onClick={() => fetchData(true)} disabled={refreshing}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)] disabled:opacity-50 transition-colors">
                                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={2} />
                                Refresh
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-0 mt-3">
                        {([
                            { key: 'overview', label: 'Overview' },
                            { key: 'turnaround', label: 'Turnaround (TAT)' },
                        ] as const).map(t => (
                            <button key={t.key} type="button" onClick={() => setTab(t.key)}
                                className="px-4 py-2.5 text-[11px] font-bold cursor-pointer border-0 bg-transparent transition-all relative"
                                style={{ color: tab === t.key ? '#059669' : 'var(--v2-text-muted)' }}>
                                {t.label}
                                {tab === t.key && (
                                    <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-t-full"
                                        style={{ background: '#059669' }} />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {tab === 'overview' && (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        <KpiCard
                            label="Today's Revenue"
                            value={formatCurrency(today.revenue)}
                            sub={`${today.orders} order${today.orders !== 1 ? 's' : ''} today`}
                            Icon={TrendingUp}
                            accent="#059669"
                            accentBg="rgba(5,150,105,0.1)"
                        />
                        <KpiCard
                            label="This Week Revenue"
                            value={formatCurrency(week.revenue)}
                            sub={`${week.orders} order${week.orders !== 1 ? 's' : ''} this week`}
                            Icon={Activity}
                            accent="#7C3AED"
                            accentBg="rgba(124,58,237,0.1)"
                            trend={week.revenue > 0 ? `${week.orders} orders` : undefined}
                        />
                        <KpiCard
                            label="Pending Results"
                            value={String(pending)}
                            sub="Awaiting pathologist review"
                            Icon={Clock}
                            accent={pending > 0 ? '#D97706' : '#059669'}
                            accentBg={pending > 0 ? 'rgba(217,119,6,0.1)' : 'rgba(5,150,105,0.1)'}
                        />
                        <KpiCard
                            label="Total Balance Due"
                            value={formatCurrency(totalBalance)}
                            sub={`Across ${orders.length} recent orders`}
                            Icon={CreditCard}
                            accent={totalBalance > 0 ? '#DC2626' : '#059669'}
                            accentBg={totalBalance > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(5,150,105,0.1)'}
                        />
                        <KpiCard
                            label="STAT Orders Pending"
                            value={String(pendingStatCount)}
                            sub={pendingStatCount > 0 ? 'Needs triage now' : 'None open'}
                            Icon={AlertTriangle}
                            accent={pendingStatCount > 0 ? '#DC2626' : '#059669'}
                            accentBg={pendingStatCount > 0 ? 'rgba(220,38,38,0.1)' : 'rgba(5,150,105,0.1)'}
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                        <div className="lg:col-span-2">
                            <RevenueComparison today={today} week={week} />
                        </div>

                        <div
                            className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-5 flex flex-col gap-4"
                            style={{ boxShadow: 'var(--v2-shadow-card)' }}
                        >
                            <p className="text-[11px] font-black uppercase tracking-widest text-[var(--v2-text-muted)] m-0">Recent Summary</p>

                            {[
                                { label: 'Total Billed', value: formatCurrency(totalAmount), color: 'var(--v2-text-heading)', Icon: TrendingUp },
                                { label: 'Total Collected', value: formatCurrency(totalAmount - totalBalance), color: '#059669', Icon: CreditCard },
                                { label: 'Balance Pending', value: formatCurrency(totalBalance), color: totalBalance > 0 ? '#DC2626' : '#059669', Icon: Clock },
                                { label: 'Orders Shown', value: String(orders.length), color: 'var(--v2-text-primary)', Icon: Beaker },
                            ].map(({ label, value, color, Icon }) => (
                                <div key={label} className="flex items-center justify-between py-1 border-b border-[var(--v2-border-divider)] last:border-0">
                                    <div className="flex items-center gap-2">
                                        <Icon className="w-3.5 h-3.5 text-[var(--v2-text-muted)]" strokeWidth={1.5} />
                                        <span className="text-[11px] text-[var(--v2-text-secondary)]">{label}</span>
                                    </div>
                                    <span className="text-[12px] font-black tabular-nums" style={{ color }}>{value}</span>
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={() => navigate('/lab/orders')}
                                className="mt-auto w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-[11px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)] transition-colors"
                            >
                                View All Orders <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                        </div>
                    </div>

                    <div
                        className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden"
                        style={{ boxShadow: 'var(--v2-shadow-card)' }}
                    >
                        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--v2-border-subtle)]">
                            <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                                    style={{ background: 'rgba(5,150,105,0.1)' }}>
                                    <Calendar className="w-3.5 h-3.5 text-[#059669]" strokeWidth={2} />
                                </div>
                                <p className="text-[13px] font-black text-[var(--v2-text-heading)] m-0">Recent Orders</p>
                                <span className="px-2 py-0.5 rounded-full text-[9px] font-black"
                                    style={{ background: 'rgba(5,150,105,0.1)', color: '#059669' }}>
                                    {orders.length}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate('/lab/orders')}
                                className="inline-flex items-center gap-1 text-[11px] font-bold cursor-pointer bg-transparent border-0 p-0 text-[var(--v2-text-muted)] hover:text-[#059669] transition-colors"
                            >
                                See all <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                            </button>
                        </div>

                        {orders.length === 0 ? (
                            <div className="flex flex-col items-center gap-2 py-16 text-center">
                                <Beaker className="w-8 h-8 text-[var(--v2-text-muted)]" strokeWidth={1.5} />
                                <p className="text-sm font-semibold text-[var(--v2-text-secondary)]">No recent orders</p>
                            </div>
                        ) : (
                            <>
                                <div className="hidden md:block overflow-x-auto">
                                    <table className="w-full border-collapse" style={{ fontSize: 12, tableLayout: 'fixed' }}>
                                        <colgroup>
                                            <col style={{ width: '20%' }} />
                                            <col style={{ width: '18%' }} />
                                            <col style={{ width: '12%' }} />
                                            <col style={{ width: '13%' }} />
                                            <col style={{ width: '13%' }} />
                                            <col style={{ width: '13%' }} />
                                            <col style={{ width: '11%' }} />
                                        </colgroup>
                                        <thead>
                                            <tr style={{ background: 'var(--v2-bg-surface)', borderBottom: '2px solid var(--v2-border-divider)' }}>
                                                {[
                                                    { label: 'Patient', note: '' },
                                                    { label: 'Invoice', note: '/ Doctor' },
                                                    { label: 'Date', note: '· Mode' },
                                                    { label: 'Total', note: '/ Net' },
                                                    { label: 'Tax', note: '/ Discount' },
                                                    { label: 'Paid', note: '/ Balance' },
                                                    { label: 'Status', note: '' },
                                                ].map(({ label, note }) => (
                                                    <th key={label}
                                                        className="text-left px-4 py-3"
                                                        style={{ fontWeight: 800 }}>
                                                        <span className="text-[10px] font-black uppercase tracking-[0.1em] text-[var(--v2-text-primary)]">{label}</span>
                                                        {note && <span className="ml-1 text-[9px] font-semibold text-[var(--v2-text-muted)] normal-case tracking-normal">{note}</span>}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {orders.map((order, idx) => {
                                                const st = statusLabel(order.orderStatus);
                                                const name = order.patientName ?? 'Unknown Patient';
                                                const words = name.trim().split(/\s+/).filter(Boolean);
                                                const initials = words.slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?';

                                                const AVATAR_COLORS = [
                                                    '#059669', '#7C3AED', '#0284C7', '#D97706',
                                                    '#DC2626', '#0891B2', '#65A30D', '#9333EA',
                                                ];
                                                const nameHash = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
                                                const avatarColor = AVATAR_COLORS[nameHash % AVATAR_COLORS.length];

                                                const total = safeNum(order.totalAmount);
                                                const net = safeNum(order.netAmount);
                                                const tax = safeNum(order.taxAmount);
                                                const disc = safeNum(order.discountAmount);
                                                const paid = safeNum(order.paidAmount);
                                                const bal = safeNum(order.balanceAmount ?? (total - paid));
                                                const isOverpaid = bal < 0;
                                                const isSettled = bal === 0 && total > 0;
                                                const displayDate = order.orderDate ?? order.createdAt?.slice(0, 10) ?? '—';
                                                const isEven = idx % 2 === 0;

                                                return (
                                                    <tr
                                                        key={order.orderId ?? order.PK ?? idx}
                                                        onClick={() => navigate('/lab/orders')}
                                                        className="cursor-pointer group"
                                                        title={order.comments ? `💬 ${order.comments}` : undefined}
                                                        style={{
                                                            borderBottom: '1px solid var(--v2-border-divider)',
                                                            background: isEven ? 'transparent' : 'rgba(0,0,0,0.012)',
                                                            transition: 'background 0.12s ease',
                                                        }}
                                                        onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--v2-bg-surface)'}
                                                        onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = isEven ? 'transparent' : 'rgba(0,0,0,0.012)'}
                                                    >
                                                        <td className="px-4 py-2.5">
                                                            <div className="flex items-center gap-2.5">
                                                                <div
                                                                    className="w-8 h-8 rounded-xl flex items-center justify-center text-[10px] font-black text-white flex-shrink-0 select-none"
                                                                    style={{ background: avatarColor }}>
                                                                    {initials}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="text-[12px] font-bold text-[var(--v2-text-primary)] m-0 truncate leading-snug capitalize">
                                                                        {name}
                                                                    </p>
                                                                    <p className="text-[10px] text-[var(--v2-text-muted)] m-0 truncate leading-snug">
                                                                        {[
                                                                            order.age && `${order.age}y`,
                                                                            order.gender ? order.gender.charAt(0).toUpperCase() + order.gender.slice(1) : null,
                                                                            order.mobile,
                                                                        ].filter(Boolean).join(' · ') || '—'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td className="px-4 py-2.5">
                                                            <p className="font-mono text-[11px] font-bold text-[var(--v2-text-primary)] m-0 truncate leading-snug">
                                                                {order.invoiceNumber ?? <span className="text-[var(--v2-text-muted)]">—</span>}
                                                            </p>
                                                            <p className="text-[10px] text-[var(--v2-text-muted)] m-0 truncate leading-snug flex items-center gap-1 mt-0.5">
                                                                <User className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={1.5} />
                                                                <span className="truncate">{order.referenceDoctor ?? '—'}</span>
                                                            </p>
                                                        </td>

                                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                                            <p className="text-[11px] font-semibold text-[var(--v2-text-primary)] m-0 leading-snug">{displayDate}</p>
                                                            <p className="text-[10px] m-0 leading-snug capitalize">
                                                                {order.paymentMode
                                                                    ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: 'var(--v2-bg-surface)', color: 'var(--v2-text-muted)', border: '1px solid var(--v2-border-subtle)' }}>
                                                                        {order.paymentMode}
                                                                    </span>
                                                                    : <span className="text-[var(--v2-text-muted)]">—</span>
                                                                }
                                                            </p>
                                                        </td>

                                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                                            <p className="text-[12px] font-black text-[var(--v2-text-primary)] m-0 tabular-nums leading-snug">
                                                                {total > 0 ? formatCurrency(total) : <span className="text-[var(--v2-text-muted)]">—</span>}
                                                            </p>
                                                            {net > 0 && (
                                                                <p className="text-[10px] text-[var(--v2-text-muted)] m-0 tabular-nums leading-snug">
                                                                    Net:&nbsp;<span className="text-[var(--v2-text-secondary)] font-semibold">{formatCurrency(net)}</span>
                                                                </p>
                                                            )}
                                                        </td>

                                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                                            <p className="text-[11px] font-semibold text-[var(--v2-text-primary)] m-0 tabular-nums leading-snug">
                                                                {tax > 0 ? formatCurrency(tax) : <span className="text-[var(--v2-text-muted)]">—</span>}
                                                            </p>
                                                            <p className="text-[10px] m-0 tabular-nums leading-snug">
                                                                {disc > 0
                                                                    ? <span style={{ color: '#059669' }} className="font-semibold">-{formatCurrency(disc)}</span>
                                                                    : <span className="text-[var(--v2-text-muted)]">—</span>
                                                                }
                                                            </p>
                                                        </td>

                                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                                            <p className="text-[11px] font-semibold m-0 tabular-nums leading-snug" style={{ color: '#059669' }}>
                                                                {formatCurrency(paid)}
                                                            </p>
                                                            {isSettled ? (
                                                                <span className="inline-flex items-center px-1.5 py-px rounded text-[8px] font-black uppercase tracking-wider"
                                                                    style={{ background: 'rgba(5,150,105,0.1)', color: '#059669' }}>
                                                                    Settled
                                                                </span>
                                                            ) : isOverpaid ? (
                                                                <p className="text-[11px] font-black m-0 tabular-nums leading-snug" style={{ color: '#7C3AED' }}>
                                                                    {formatCurrency(Math.abs(bal))}
                                                                    <span className="text-[8px] ml-1 font-bold opacity-70">credit</span>
                                                                </p>
                                                            ) : (
                                                                <p className="text-[11px] font-black m-0 tabular-nums leading-snug" style={{ color: '#DC2626' }}>
                                                                    {formatCurrency(bal)}
                                                                    <span className="text-[8px] ml-1 font-bold opacity-70">due</span>
                                                                </p>
                                                            )}
                                                        </td>

                                                        <td className="px-4 py-2.5">
                                                            <div className="flex flex-col gap-1 items-start">
                                                                <span
                                                                    className="inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider whitespace-nowrap"
                                                                    style={{
                                                                        background: st.bg,
                                                                        color: st.color,
                                                                        border: `1px solid ${st.color}30`,
                                                                    }}>
                                                                    {order.orderStatus ?? 'pending'}
                                                                </span>
                                                                {normalizeOrderPriority(order.priority) !== 'routine' && (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider whitespace-nowrap"
                                                                        style={{ background: PRIORITY_META[normalizeOrderPriority(order.priority)].bg, color: PRIORITY_META[normalizeOrderPriority(order.priority)].color }}>
                                                                        {normalizeOrderPriority(order.priority) === 'stat' && <AlertTriangle className="w-2 h-2" strokeWidth={3} />}
                                                                        {PRIORITY_META[normalizeOrderPriority(order.priority)].label}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="md:hidden divide-y divide-[var(--v2-border-divider)]">
                                    {orders.map((order, idx) => (
                                        <RecentOrderRow
                                            key={order.orderId ?? idx}
                                            order={order}
                                            onClick={() => navigate('/lab/orders')}
                                        />
                                    ))}
                                </div>
                            </>
                        )}

                        {orders.length > 0 && (
                            <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--v2-border-subtle)]"
                                style={{ background: 'var(--v2-bg-surface)' }}>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">
                                    {orders.length} order{orders.length !== 1 ? 's' : ''} shown
                                </span>
                                <div className="flex items-center gap-5 text-[11px] font-bold">
                                    <span className="text-[var(--v2-text-muted)]">
                                        Billed:&nbsp;
                                        <span className="font-black text-[var(--v2-text-primary)] tabular-nums">{formatCurrency(totalAmount)}</span>
                                    </span>
                                    <span className="text-[var(--v2-text-muted)]">
                                        Balance:&nbsp;
                                        <span className="font-black tabular-nums" style={{ color: totalBalance > 0 ? '#DC2626' : '#059669' }}>
                                            {formatCurrency(totalBalance)}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {tab === 'turnaround' && (
                <div className="flex flex-col gap-4">
                    {tatLoading ? (
                        <div className="flex flex-col items-center justify-center py-16 gap-2">
                            <div className="w-6 h-6 rounded-full border-2 border-[#059669] border-t-transparent animate-spin" />
                            <p className="text-[11px] text-[var(--v2-text-muted)] font-semibold">Computing turnaround times…</p>
                        </div>
                    ) : tatSamples.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 py-16 text-center rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)]" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                            <Timer className="w-8 h-8 text-[var(--v2-text-muted)]" strokeWidth={1.5} />
                            <p className="text-sm font-semibold text-[var(--v2-text-secondary)]">Not enough completed orders yet</p>
                            <p className="text-[11px] text-[var(--v2-text-muted)] max-w-xs">
                                Turnaround is approximated from each order's created-to-last-updated timestamps — no explicit "completed at" field exists yet, so this improves as more orders complete.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                <KpiCard
                                    label="Average TAT"
                                    value={`${avgTat.toFixed(1)}h`}
                                    sub={`Across ${tatSamples.length} completed orders`}
                                    Icon={Timer}
                                    accent="#045EB8"
                                    accentBg="rgba(4,94,184,0.1)"
                                />
                                <KpiCard
                                    label="Median TAT"
                                    value={`${medianTat.toFixed(1)}h`}
                                    sub="Typical turnaround time"
                                    Icon={Clock}
                                    accent="#7C3AED"
                                    accentBg="rgba(124,58,237,0.1)"
                                />
                                <KpiCard
                                    label="STAT Orders Pending"
                                    value={String(pendingStatCount)}
                                    sub={pendingStatCount > 0 ? 'Needs triage now' : 'None open'}
                                    Icon={AlertTriangle}
                                    accent={pendingStatCount > 0 ? '#DC2626' : '#059669'}
                                    accentBg={pendingStatCount > 0 ? 'rgba(220,38,38,0.1)' : 'rgba(5,150,105,0.1)'}
                                />
                            </div>

                            <div className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-5 flex flex-col gap-3"
                                style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                                <p className="text-[11px] font-black uppercase tracking-widest text-[var(--v2-text-muted)] m-0">
                                    Turnaround by Order (most recent {Math.min(tatOrders.length, 20)})
                                </p>
                                <p className="text-[10px] text-[var(--v2-text-muted)] m-0 -mt-2">
                                    Approximated from order created → last updated timestamps
                                </p>
                                <div className="flex flex-col gap-2">
                                    {tatOrders.slice(0, 20).map(o => {
                                        const hrs = elapsedHours(o.orderDate ?? o.createdAt ?? (o as any).created_at, (o as any).updatedAt);
                                        if (hrs === null) return null;
                                        const pct = Math.min(100, (hrs / maxTat) * 100);
                                        const over = hrs > avgTat * 1.5;
                                        return (
                                            <div key={extractLabOrderId(o)} className="flex items-center gap-3">
                                                <span className="text-[11px] font-semibold text-[var(--v2-text-secondary)] w-32 truncate flex-shrink-0">
                                                    {extractPatientName(o)}
                                                </span>
                                                <div className="flex-1 h-2.5 rounded-full bg-[var(--v2-bg-surface)] overflow-hidden">
                                                    <div className="h-full rounded-full transition-all duration-700"
                                                        style={{ width: `${pct}%`, background: over ? '#DC2626' : '#059669' }} />
                                                </div>
                                                <span className="text-[11px] font-black tabular-nums w-14 text-right flex-shrink-0" style={{ color: over ? '#DC2626' : 'var(--v2-text-primary)' }}>
                                                    {hrs.toFixed(1)}h
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default LabDashboard;
